// src/pages/VisitProgress.jsx
import React, { useEffect, useMemo, useState } from "react";
import CallNumber from "../components/CallNumber";
import { supabase } from "../lib/supabase";
import "../components/CallNumber.css";

// 與 Schedule.jsx 一致（僅作顯示用）
const SESSIONS = [
  { key: "morning", label: "早診", time: "08:30–12:00" },
  { key: "afternoon", label: "午診", time: "14:30–17:30" },
  { key: "evening", label: "晚診", time: "18:30–21:30" },
];

// ===== 參考 Time.jsx 的判斷（含結束時間 <=） =====
function toMinutes(h, m) {
  return h * 60 + m;
}
function getSessionInfo(date = new Date()) {
  const current = date.getHours() * 60 + date.getMinutes();
  const ranges = [
    {
      start: toMinutes(8, 30),
      end: toMinutes(12, 0),
      label: "早診",
      key: "morning",
      time: "08:30–12:00",
      icon: "/icons/morning-shift-icon.png",
    },
    {
      start: toMinutes(14, 30),
      end: toMinutes(17, 30),
      label: "午診",
      key: "afternoon",
      time: "14:30–17:30",
      icon: "/icons/noon-shift-icon.png",
    },
    {
      start: toMinutes(18, 30),
      end: toMinutes(21, 30),
      label: "晚診",
      key: "evening",
      time: "18:30–21:30",
      icon: "/icons/night-shift-icon.png",
    },
  ];
  for (const r of ranges) {
    if (current >= r.start && current <= r.end) {
      return {
        isOpen: true,
        sessionKey: r.key,
        message: `${r.label}看診中`,
        label: r.label,
        time: r.time,
        icon: r.icon,
      };
    }
  }
  return {
    isOpen: false,
    sessionKey: null,
    message: "休診中",
    label: "",
    time: "",
    icon: "/icons/off-shift-icon.png",
  };
}
function toDateKey(dt = new Date()) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ✅ 單一診間卡片（標題在「無醫師 / 非看診」時改為：第一/第二診間 休診中）
function RoomPanel({ roomNo, deviceId, doctorName, open }) {
  const hasDoctor = !!(doctorName && doctorName !== "—" && doctorName !== "-");
  const showCallNumber = open && hasDoctor;
  const roomLabel = roomNo === 1 ? "第一診間" : "第二診間";
  const titleText = showCallNumber
    ? `${roomLabel}　看診醫師：${doctorName}`
    : `${roomLabel}　休診中`;

  return (
    <section className="cn-card vp-room">
      <div className="cn-header" style={{ justifyContent: "space-between" }}>
        <div className="cn-title">{titleText}</div>
      </div>

      {showCallNumber ? (
        <CallNumber id={String(deviceId)} label="" size="sm" />
      ) : (
        // 休診版：整塊淡粉色、同尺寸、置中「休診中」
        <div className="cn-card">
          <div
            className="cn-display compact fit4 vp-closed"
            role="group"
            aria-label="目前看診燈號"
          >
            <span className="vp-closed-text">休診中</span>
          </div>
        </div>
      )}
    </section>
  );
}

export default function VisitProgress() {
  const [now, setNow] = useState(new Date());
  const sessionInfo = useMemo(() => getSessionInfo(now), [now]);
  const isOpen = sessionInfo.isOpen;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [room1Doctor, setRoom1Doctor] = useState("");
  const [room2Doctor, setRoom2Doctor] = useState("");

  // 秒鐘更新
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // 讀今天 roster（同 Schedule.jsx）
  useEffect(() => {
    let alive = true;
    async function fetchTodayRoster() {
      setLoading(true);
      setError("");

      if (!isOpen) {
        // 非看診時間 → 直接休診
        if (!alive) return;
        setRoom1Doctor("");
        setRoom2Doctor("");
        setLoading(false);
        return;
      }
      try {
        const today = toDateKey();
        const { data, error: qErr } = await supabase
          .from("roster")
          .select("morning,afternoon,evening")
          .eq("date", today)
          .single();
        if (qErr) throw qErr;

        const arr = Array.isArray(data?.[sessionInfo.sessionKey])
          ? data[sessionInfo.sessionKey]
          : [];
        const d1 = (arr[0] || "").trim();
        const d2 = (arr[1] || "").trim();

        if (!alive) return;
        setRoom1Doctor(d1);
        setRoom2Doctor(d2); // 沒有醫師 ⇒ 空字串 ⇒ 休診
        setLoading(false);
      } catch (e) {
        console.error("[VisitProgress] roster fetch error:", e);
        if (!alive) return;
        setRoom1Doctor("");
        setRoom2Doctor("");
        setError("讀取班表失敗");
        setLoading(false);
      }
    }

    fetchTodayRoster();
    const r = setInterval(fetchTodayRoster, 60000); // 每分鐘刷新
    return () => {
      alive = false;
      clearInterval(r);
    };
  }, [isOpen, sessionInfo.sessionKey]);

  // 頂部顯示
  const dateString =
    `${now.getFullYear()}年${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}月${String(now.getDate()).padStart(2, "0")}日 ` +
    now.toLocaleDateString("zh-TW", { weekday: "long" });
  const timeString = now.toLocaleTimeString("zh-TW", { hour12: false });

  return (
    <section style={{ padding: "8px 0" }}>
      {/* 頁首：純白、靠左；看診狀態樣式沿用 Time.jsx 的 .time-status */}
      <div className="vp-head">
        <div className="vp-head-line">
          <span className="vp-head-date">{dateString}</span>
          {/* <span className="vp-head-time">{timeString}</span> */}
          <span className={`time-status ${isOpen ? "open" : "closed"}`}>
            <img
              className="time-icon"
              src={sessionInfo.icon}
              alt={isOpen ? sessionInfo.message : "休診中"}
              title={isOpen ? sessionInfo.message : "休診中"}
            />
          </span>
          {loading ? <span className="vp-hint">（讀取中…）</span> : null}
          {error ? <span className="vp-hint">（{error}）</span> : null}
        </div>
      </div>

      {/* 兩個診間：桌面並排 / 行動直排 */}
      <div className="vp-grid">
        <RoomPanel
          roomNo={1}
          deviceId={3500}
          doctorName={room1Doctor}
          open={isOpen && !!room1Doctor}
        />
        <RoomPanel
          roomNo={2}
          deviceId={3501}
          doctorName={room2Doctor}
          open={isOpen && !!room2Doctor}
        />
      </div>
      <div className="vp-mainpi-link-wrap">
        <a
          href="https://www.mainpi.com/query?i=3500"
          target="_blank"
          rel="noopener noreferrer"
          className="vp-mainpi-link"
        >
          看診進度無法顯示請點此查看
        </a>
      </div>
    </section>
  );
}
